/**
 * An event's races as broadcast elements and the host's console name them: one per course, in its course colour (the
 * map's, `courses.ts`), with a short label ("10 km") and a full name. Leaderboards filter on a race and a gender.
 */

import { courseColor, courseKm, drawnCourses, type CourseMapRoute } from '@/components/map/course-map/courses'
import type { RaceGender } from './graphics'

export interface Race {
  id: string
  /** The short label: the distance its name gives, else its length, else its name ("10 km"). */
  label: string
  /** Its full name ("The Cobra 10 km"). */
  name: string
  /** Its course colour, as on the map. */
  color: string
  /** Its length in kilometres, when known. */
  distanceKm: number | null
}

const kilometres = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 })

/**
 * The event's races, in the payload's order (primary first): every course with an id. Several courses take the map's
 * colours and legend labels; one course takes the first course colour, as its athletes' dots do.
 */
export function eventRaces(routes: readonly CourseMapRoute[] | null | undefined): Race[] {
  const courses = (routes ?? []).filter((route): route is CourseMapRoute & { id: string } => Boolean(route.id))
  const drawn = drawnCourses(courses)
  return courses.map((route, index) => {
    const course = drawn?.find((each) => each.route.id === route.id)
    const km = courseKm(route)
    const name = route.name?.trim() || (km ? `${kilometres.format(km)} km` : `Course ${index + 1}`)
    return {
      id: route.id,
      label: course?.label ?? (km ? `${kilometres.format(km)} km` : name),
      name: course?.name ?? name,
      color: course?.color ?? courseColor(index),
      distanceKm: typeof route.distance_km === 'number' && route.distance_km > 0 ? route.distance_km : km,
    }
  })
}

/** The race with this id, or null for one the event doesn't have (or no id). */
export function raceById(races: readonly Race[], routeId: string | null | undefined): Race | null {
  return (routeId && races.find((race) => race.id === routeId)) || null
}

const GENDER_WORD: Record<RaceGender, string> = { female: 'Women', male: 'Men' }

/** Who a board ranks, in a word: "Women", "Men", or "Everyone" without a gender. */
export function genderWord(gender: RaceGender | null | undefined): string {
  return gender ? GENDER_WORD[gender] : 'Everyone'
}

/**
 * A board's title: the host's own, else its race, with the gender before it when it has one ("Women · 10 km", "10 km").
 */
export function standingsTitle(race: Race, gender: RaceGender | null | undefined, title?: string | null): string {
  const own = title?.trim()
  if (own) return own
  return gender ? `${GENDER_WORD[gender]} · ${race.label}` : race.label
}
